import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { X, Sparkles, AlertCircle, Flame } from 'lucide-react';
import IgnitingLoader from './IgnitingLoader';
import IntervalList from './IntervalList';
import { localISO } from '../data/programs';
import { useProfile } from '../context/ProfileContext';
import { useAuth, formatApiError } from '../context/AuthContext';

const AI_CONSENT_REMINDER_KEY = 'enferno_ai_consent_shown_v1';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AIEnduranceModal — endurance sibling of AIWorkoutModal. Triggered from the
 * "Generate AI session" button on interval-based endurance surfaces. Calls
 * /api/ai/endurance with the activity/mode/surface + time budget, then renders
 * the returned `intervals` through the SAME IntervalList component the static
 * library uses — so AI and hand-authored sessions look identical.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   activity: string     — e.g. "Walk/Run"
 *   mode: string|null    — 'indoor' | 'outdoor' | null
 *   surface: string|null — e.g. "Road", "Trail", "Pool"
 *   minutes: number      — time budget for the session
 *   units: 'imperial'|'metric'
 *   onApply: (session) => void — receives the generated session to log
 */
const AIEnduranceModal = ({ open, onClose, activity, mode, surface, minutes = 30, units = 'imperial', onApply }) => {
  const { history } = useProfile();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState(null);
  const [needsConsentNotice, setNeedsConsentNotice] = useState(() => {
    try { return !localStorage.getItem(AI_CONSENT_REMINDER_KEY); } catch { return false; }
  });

  const label = [activity, mode ? (mode === 'outdoor' ? 'Outdoor' : 'Indoor') : null, surface]
    .filter(Boolean).join(' · ');

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai/usage`);
      setUsage(data);
    } catch { /* non-critical — meter just stays hidden */ }
  }, []);

  const generate = async () => {
    setBusy(true);
    setError('');
    setSession(null);
    try {
      const recent = (history || []).slice(0, 5).map((h) => h.title).filter(Boolean);
      // User-LOCAL date (same helper that keys painLog / wellbeingLog) so the
      // backend resolves "today's" check-ins correctly regardless of server TZ.
      const clientDate = localISO();
      const { data } = await axios.post(`${API}/ai/endurance`, {
        activity,
        mode: mode || null,
        surface: surface || null,
        minutes,
        recent_titles: recent,
        client_date: clientDate,
      });
      setSession(data.session);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const dismissConsent = () => {
    try { localStorage.setItem(AI_CONSENT_REMINDER_KEY, '1'); } catch { /* ignore */ }
    setNeedsConsentNotice(false);
    generate();
  };

  // Fetch usage on open so the caption is ready before / after generation.
  useEffect(() => {
    if (open && user) loadUsage();
  }, [open, user, loadUsage]);

  // Auto-trigger on first open — but only after consent notice is dismissed.
  useEffect(() => {
    if (open && !needsConsentNotice && !session && !busy && !error) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsConsentNotice]);

  // Reset when the target activity/surface changes so a stale session never
  // carries over between different surfaces.
  useEffect(() => {
    setSession(null);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, mode, surface]);

  // Usage caption shares the strength AI-workout monthly budget (backend meters
  // both under "workout"). Same trial / near-cap surfacing rules.
  const usageCaption = (() => {
    if (!usage || usage.is_admin || usage.workouts_limit == null) return null;
    const remaining = Math.max(0, usage.workouts_limit - usage.workouts_used);
    const isTrial = usage.workout_period === 'trial';
    const nearCap = usage.workouts_used / usage.workouts_limit >= 0.7;
    if (!isTrial && !nearCap) return null;
    if (remaining === 0) {
      return isTrial
        ? 'Trial AI sessions complete — your saved ones are yours to keep. Enferno keeps the coach adapting to your check-ins.'
        : 'Fresh AI sessions arrive at the start of next month — your saved sessions and full library are open in the meantime.';
    }
    return isTrial
      ? `${remaining} of ${usage.workouts_limit} free trial AI session${remaining === 1 ? '' : 's'} remaining.`
      : `${remaining} AI session${remaining === 1 ? '' : 's'} left this month.`;
  })();

  const trialExhausted = !!usage && !usage.is_admin && usage.workouts_limit != null
    && usage.workout_period === 'trial' && (usage.workouts_limit - usage.workouts_used) <= 0;

  if (!open) return null;

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 text-center">
          <Sparkles className="w-8 h-8 text-enf-accent-fg mx-auto mb-3" />
          <div className="font-display text-3xl tracking-wide mb-2">Sign in required</div>
          <p className="text-enf-text-secondary text-sm mb-4">AI-generated sessions are an Enferno feature for signed-in athletes.</p>
          <button onClick={onClose} className="px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl"
        data-testid="ai-endurance-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-enf-surface-deep border-b border-enf-surface-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-enf-accent flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-enf-text-primary" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-enf-accent-fg font-bold">AI generated</div>
              <div className="text-sm font-semibold">{label} · {minutes} min</div>
            </div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary p-1" aria-label="Close" data-testid="ai-endurance-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {needsConsentNotice && !busy && !session && !error && (
            <div className="text-center py-4">
              <Sparkles className="w-8 h-8 text-enf-accent-fg mx-auto mb-3" />
              <div className="text-xs font-semibold uppercase tracking-widest text-enf-text-secondary mb-2">AI Data Notice</div>
              <h3 className="font-display text-2xl tracking-wide mb-3">Your profile powers the AI</h3>
              <p className="text-sm text-enf-text-secondary leading-relaxed mb-6 max-w-sm mx-auto">
                Enferno AI uses your training profile — experience, goals, and injury flags — to personalize this session. You can review or change this in{' '}
                <Link to="/profile" className="text-enf-role-info hover:underline" onClick={onClose}>
                  Profile → Your Data &amp; Consents
                </Link>.
              </p>
              <button
                type="button"
                onClick={dismissConsent}
                className="px-6 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
                data-testid="ai-endurance-consent-got-it"
              >
                Got it — generate my session
              </button>
            </div>
          )}

          {busy && (
            <IgnitingLoader label="Igniting your session..." />
          )}

          {error && !busy && (
            <div className="px-4 py-3 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger flex items-start gap-2" data-testid="ai-endurance-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold mb-0.5">The coach is taking a breath</div>
                <div className="text-enf-text-secondary">{error}</div>
                <button onClick={generate} className="text-xs text-enf-role-info hover:text-enf-text-primary mt-2">Light it again</button>
              </div>
            </div>
          )}

          {session && !busy && (
            <div data-testid="ai-endurance-content">
              <div className="font-display text-4xl tracking-wide leading-tight mb-2">{session.title}</div>
              {session.focus && <div className="text-sm text-enf-text-secondary mb-3">{session.focus}</div>}
              {session.coach_intro && (
                <div className="bg-enf-accent/5 border border-enf-accent-fg/20 rounded-md p-3 mb-3 text-sm text-enf-text-primary italic leading-relaxed">
                  "{session.coach_intro}"
                </div>
              )}
              {session.why_this_workout && (
                <details className="mb-5 group" data-testid="ai-endurance-reasoning">
                  <summary className="cursor-pointer list-none flex items-center gap-2 text-xs uppercase tracking-widest text-enf-role-info font-semibold hover:text-enf-text-primary transition-colors">
                    <span className="inline-block w-3.5 h-3.5 rounded-full border border-enf-role-info/50 flex items-center justify-center text-[10px] group-open:rotate-45 transition-transform">?</span>
                    Why this session?
                  </summary>
                  <div className="mt-2 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-secondary leading-relaxed">
                    {session.why_this_workout}
                  </div>
                </details>
              )}

              <div className="mb-5">
                <IntervalList intervals={session.intervals} units={units} />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-enf-surface-border">
                <button onClick={onClose}
                  className="flex-1 px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors"
                  data-testid="ai-endurance-close-blueprint"
                >
                  Close
                </button>
                {onApply && (
                  <button onClick={() => { onApply(session); onClose(); }}
                    className="flex-1 px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    data-testid="ai-endurance-apply"
                  >
                    <Flame className="w-4 h-4 scale-x-[-1]" /> Log this session
                  </button>
                )}
                <button onClick={generate}
                  className="px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  title="Regenerate"
                  data-testid="ai-endurance-regenerate"
                >
                  <Sparkles className="w-4 h-4" /> Regenerate
                </button>
              </div>
            </div>
          )}
          {usageCaption && (
            <p className="mt-4 text-center text-[11px] text-enf-text-muted">
              {usageCaption}
              {trialExhausted && (
                <>{' '}<Link to="/pricing" onClick={onClose} className="text-enf-role-info hover:underline font-medium">See plans</Link></>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIEnduranceModal;
